import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import * as catalogoApi from "../../api/catalogo";
import { DispositivoCatalogo, DocumentoTipo } from "../../api/catalogo";
import { downloadProtectedFile } from "../../api/files";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { Badge } from "./Badge";

const DOCUMENTO_LABELS: Record<DocumentoTipo, string> = {
  scheda_tecnica: "Scheda tecnica",
  disegno: "Disegno",
  foto: "Foto",
  manuale: "Manuale",
  video: "Video",
};

/** "60 × 55 × 90 cm" oppure null se manca una delle tre misure. */
function formatTerna(l: string | null, p: string | null, h: string | null): string | null {
  if (!l || !p || !h) return null;
  const n = (v: string) => String(Number(v));
  return `${n(l)} × ${n(p)} × ${n(h)} cm`;
}

interface DispositivoMaterialeProps {
  dispositivoId: string;
}

/**
 * Consultazione read-only della documentazione di un modello del Catalogo
 * Dispositivi (manuali/foto/video/schede tecniche/misure) — usata nei moduli
 * operativi (Sopralluoghi/Installazioni/Attivazioni/Interventi) una volta
 * scelto un modello, così il caricamento dei file resta riservato alla
 * pagina Catalogo (admin/staff) ma la consultazione è aperta a tutti.
 */
export function DispositivoMateriale({ dispositivoId }: DispositivoMaterialeProps) {
  const { showToast } = useToast();
  const [dispositivo, setDispositivo] = useState<DispositivoCatalogo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    catalogoApi
      .getDispositivo(dispositivoId)
      .then(({ dispositivo: d }) => {
        if (!cancelled) setDispositivo(d);
      })
      .catch(() => {
        if (!cancelled) setDispositivo(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispositivoId]);

  if (isLoading) {
    return <p className="text-xs text-ink/40">Caricamento documentazione…</p>;
  }

  if (!dispositivo) {
    return null;
  }

  const tipiConDocumenti = (Object.keys(DOCUMENTO_LABELS) as DocumentoTipo[]).filter(
    (tipo) => dispositivo.documenti.filter((doc) => doc.tipo === tipo).length > 0
  );

  return (
    <div className="rounded-lg border border-line bg-primary/5 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ink">
            {dispositivo.marca} {dispositivo.modello}
          </p>
          {dispositivo.sigla && <p className="text-xs text-ink/50">Sigla {dispositivo.sigla}</p>}
        </div>
        <Badge color="primary">{dispositivo.tipo_dispositivo}</Badge>
      </div>

      {(() => {
        const cassaforte = formatTerna(
          dispositivo.misure_cassaforte_lunghezza,
          dispositivo.misure_cassaforte_profondita,
          dispositivo.misure_cassaforte_altezza
        );
        const ingombro = formatTerna(
          dispositivo.misure_ingombro_lunghezza,
          dispositivo.misure_ingombro_profondita,
          dispositivo.misure_ingombro_altezza
        );
        if (!cassaforte && !ingombro) return null;
        return (
          <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/70">
            {cassaforte && (
              <span>
                <span className="font-semibold text-ink/50">Cassaforte:</span> {cassaforte}
              </span>
            )}
            {ingombro && (
              <span>
                <span className="font-semibold text-ink/50">Ingombro:</span> {ingombro}
              </span>
            )}
          </div>
        );
      })()}

      {tipiConDocumenti.length === 0 ? (
        <p className="text-xs text-ink/40">Nessuna documentazione disponibile per questo modello.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {tipiConDocumenti.map((tipo) => (
            <div key={tipo}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink/50">
                {DOCUMENTO_LABELS[tipo]}
              </p>
              <ul className="flex flex-col gap-1">
                {dispositivo.documenti
                  .filter((doc) => doc.tipo === tipo)
                  .map((doc) => (
                    <li key={doc.id}>
                      <button
                        type="button"
                        onClick={() =>
                          downloadProtectedFile(doc.file_url, doc.nome_file ?? "documento").catch((err) =>
                            showToast(getErrorMessage(err, "Impossibile scaricare il documento."), "error")
                          )
                        }
                        className="flex w-full items-center gap-1.5 truncate rounded bg-white px-2 py-1.5 text-left text-xs text-primary hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span className="truncate">{doc.nome_file}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
